'use client';

import { useEffect, useMemo, useState } from 'react';
import type React from 'react';
import { Check, ChevronDown, Circle, Loader2, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import customFetch from '@/lib/utils/fetch';
import { useTranslation } from '@/lib/i18n';
import {
  FeedbackOption,
  FeedbackSubmissionInput,
  getFeedbackConfig,
  getFeedbackIssueTypes,
  getMessageFeedbackReasons,
  normalizeFeedbackSubmission,
} from '@/lib/feedback/shared';
import { useSnackbar } from '@/contexts/SnackbarContext';

export type FeedbackDialogProps = {
  isOpen?: boolean;
  defaultIssueType?: string;
  defaultDescription?: string;
  defaultEmail?: string;
  onClose?: () => void;
  className?: string;
};

export type MessageFeedbackDialogProps = {
  isOpen?: boolean;
  defaultReasonIds?: string[];
  defaultDescription?: string;
  defaultEmail?: string;
  conversationContext?: Record<string, any>;
  onClose?: () => void;
  onSubmit?: (feedback: ReturnType<typeof normalizeFeedbackSubmission>) => void | Promise<void>;
  className?: string;
};

const config = getFeedbackConfig();
const EMPTY_REASON_IDS: string[] = [];

export function FeedbackDialog({
  isOpen = false,
  defaultIssueType,
  defaultDescription = '',
  defaultEmail = '',
  onClose,
  className,
}: FeedbackDialogProps) {
  const { t } = useTranslation();
  const { showSuccess } = useSnackbar();
  const issueTypes = getFeedbackIssueTypes();
  const [mounted, setMounted] = useState(false);
  const [issueType, setIssueType] = useState(defaultIssueType || '');
  const [description, setDescription] = useState(defaultDescription);
  const [email, setEmail] = useState(defaultEmail);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [issueTypeError, setIssueTypeError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const selectedIssue = useMemo(
    () => issueTypes.find((item) => item.value === issueType),
    [issueType, issueTypes],
  );
  const canSubmit = Boolean(description.trim());

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setIssueType(defaultIssueType || '');
    setDescription(defaultDescription);
    setEmail(defaultEmail);
    setDropdownOpen(false);
    setIssueTypeError('');
    setDescriptionError('');
    setSubmitError('');
  }, [defaultDescription, defaultEmail, defaultIssueType, isOpen]);

  if (!isOpen || !mounted) return null;

  const handleSubmit = async () => {
    if (submitting) return;

    if (!issueType) {
      setIssueTypeError(`${t(config.general.issueTypeLabel)} ${t('is required')}`);
      return;
    }

    if (!description.trim()) {
      setDescriptionError(`${t(config.general.descriptionLabel)} ${t('is required')}`);
      return;
    }

    setDescriptionError('');

    setSubmitting(true);
    setSubmitError('');
    try {
      const feedback = normalizeFeedbackSubmission({
        surface: 'general',
        sentiment: 'neutral',
        issueType,
        description,
        email: email.trim() || defaultEmail.trim(),
      });
      const response = await customFetch('/api/feedback', {
        method: 'POST',
        body: JSON.stringify({ feedback }),
      });

      if (!(response instanceof Response) || !response.ok) {
        throw new Error('Failed to submit feedback');
      }
      showSuccess(t('Thanks for your feedback. Your input helps us improve future responses.'));
      onClose?.();
    } catch (error) {
      console.error('[FeedbackDialog] Failed to submit feedback:', error);
      setSubmitError(t('Failed to submit feedback. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <FeedbackOverlay className={className} onClose={onClose}>
      <FeedbackShell onClose={onClose}>
        <div className="flex flex-col gap-6 px-5 pt-5">
          <Field label={t(config.general.issueTypeLabel)}>
            <div className="relative">
              <button
                type="button"
                className={cn(
                  'flex h-9 w-full items-center justify-between rounded-lg border border-[#3F3F47] bg-[#27272A] px-3 text-left text-sm font-medium leading-5 transition-colors',
                  selectedIssue ? 'text-[#D4D4D8]' : 'text-[#71717B]',
                  dropdownOpen && 'border-[#8B3FFC]',
                )}
                onClick={() => setDropdownOpen((open) => !open)}
              >
                <span>
                  {selectedIssue ? t(selectedIssue.label) : t(config.general.issueTypePlaceholder)}
                </span>
                <ChevronDown
                  className={cn(
                    'size-4 text-[#717182] opacity-50 transition-transform',
                    dropdownOpen && 'rotate-180',
                  )}
                  strokeWidth={1.6}
                />
              </button>

              {dropdownOpen && (
                <IssueTypeMenu
                  options={issueTypes}
                  value={issueType}
                  onSelect={(value) => {
                    setIssueType(value);
                    setIssueTypeError('');
                    setDropdownOpen(false);
                  }}
                />
              )}
            </div>
            {issueTypeError && (
              <p className="mt-1 text-xs font-normal leading-4 text-[#FF5C5C]">{issueTypeError}</p>
            )}
          </Field>

          <Field label={t(config.general.descriptionLabel)}>
            <FeedbackTextarea
              value={description}
              placeholder={t(config.general.descriptionPlaceholder)}
              error={descriptionError}
              onValueChange={(value) => {
                setDescription(value);
                if (descriptionError) setDescriptionError('');
              }}
            />
          </Field>

          <Field label={t(config.general.emailLabel)}>
            <input
              value={email}
              placeholder="user@gmail.com"
              className="h-11 w-full rounded-lg border border-[#3F3F47] bg-[#27272A] px-3 text-sm font-normal leading-5 text-[#D4D4D8] outline-none placeholder:text-[#71717B] focus:border-[#8B3FFC]"
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>

          <FeedbackActions
            submitLabel={t(config.dialog.sendLabel)}
            disabled={!canSubmit || submitting}
            loading={submitting}
            onCancel={onClose}
            onSubmit={handleSubmit}
          />
          {submitError && (
            <p className="-mt-4 text-xs font-normal leading-4 text-[#FF5C5C]">{submitError}</p>
          )}
        </div>
      </FeedbackShell>
    </FeedbackOverlay>,
    document.body,
  );
}

export function MessageFeedbackDialog({
  isOpen = false,
  defaultReasonIds = EMPTY_REASON_IDS,
  defaultDescription = '',
  defaultEmail = '',
  conversationContext,
  onClose,
  onSubmit,
  className,
}: MessageFeedbackDialogProps) {
  const { t } = useTranslation();
  const { showSuccess } = useSnackbar();
  const reasons = getMessageFeedbackReasons();
  const [mounted, setMounted] = useState(false);
  const [selectedReasons, setSelectedReasons] = useState<string[]>(defaultReasonIds);
  const [description, setDescription] = useState(defaultDescription);
  const [conversationIncluded, setConversationIncluded] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const canSubmit = selectedReasons.length > 0 || Boolean(description.trim());

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedReasons(defaultReasonIds);
    setDescription(defaultDescription);
    setConversationIncluded(true);
    setSubmitError('');
  }, [defaultDescription, defaultReasonIds, isOpen]);

  if (!isOpen || !mounted) return null;

  const handleReasonToggle = (value: string) => {
    setSelectedReasons((current) =>
      current.includes(value) ? current.filter((item) => item !== value) : [...current, value],
    );
  };

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const feedback = normalizeFeedbackSubmission({
        surface: 'message_response',
        sentiment: 'negative',
        reasonIds: selectedReasons,
        description,
        email: defaultEmail.trim(),
        ...(conversationIncluded
          ? {
              conversationIncluded: true,
              conversationContext,
            }
          : {}),
      });

      if (onSubmit) {
        await onSubmit(feedback);
      } else {
        const response = await customFetch('/api/feedback', {
          method: 'POST',
          body: JSON.stringify({ feedback }),
        });

        if (!(response instanceof Response) || !response.ok) {
          throw new Error('Failed to submit feedback');
        }
      }
      showSuccess(t('Thanks for your feedback. Your input helps us improve future responses.'));

      onClose?.();
    } catch (error) {
      console.error('[MessageFeedbackDialog] Failed to submit feedback:', error);
      setSubmitError(t('Failed to submit feedback. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <FeedbackOverlay className={className} onClose={onClose}>
      <FeedbackShell onClose={onClose}>
        <div className="px-5 pt-5">
          <div className="text-sm font-medium leading-5 text-white">
            {t(config.message.question)}
          </div>

          <div className="mt-4 flex flex-wrap gap-3">
            {reasons.map((reason) => (
              <ReasonChip
                key={reason.value}
                reason={reason}
                selected={selectedReasons.includes(reason.value)}
                onClick={() => handleReasonToggle(reason.value)}
              />
            ))}
          </div>

          <div className="mt-6">
            <Field label={t(config.message.detailsLabel)}>
              <FeedbackTextarea
                value={description}
                placeholder={t(config.message.placeholder)}
                onValueChange={setDescription}
              />
            </Field>
          </div>

          <button
            type="button"
            className="mt-3 flex items-start gap-2 text-left text-xs font-normal leading-4 text-[#757575] transition-colors hover:text-[#A1A1AA]"
            onClick={() => setConversationIncluded((current) => !current)}
          >
            <SelectionIcon selected={conversationIncluded} />
            <span>{t(config.message.privacyNotice)}</span>
          </button>

          <FeedbackActions
            submitLabel={t(config.dialog.submitLabel)}
            disabled={!canSubmit || submitting}
            loading={submitting}
            onCancel={onClose}
            onSubmit={handleSubmit}
            className="mt-5"
          />
          {submitError && (
            <p className="mt-2 text-xs font-normal leading-4 text-[#FF5C5C]">{submitError}</p>
          )}
        </div>
      </FeedbackShell>
    </FeedbackOverlay>,
    document.body,
  );
}

function FeedbackOverlay({
  children,
  className,
  onClose,
}: {
  children: React.ReactNode;
  className?: string;
  onClose?: () => void;
}) {
  return (
    <div
      className={cn(
        'fixed inset-0 z-150 flex items-center justify-center bg-black/50 px-4',
        className,
      )}
      onClick={onClose}
    >
      {children}
    </div>
  );
}

function FeedbackShell({ children, onClose }: { children: React.ReactNode; onClose?: () => void }) {
  const { t } = useTranslation();

  return (
    <section
      className="w-full max-w-125 overflow-hidden rounded-[14px] border border-[#333333] bg-[#1A1A1A] text-white shadow-2xl"
      onClick={(event) => event.stopPropagation()}
    >
      <header className="flex h-13 items-center justify-between border-b border-[#333333] px-5">
        <h2 className="text-base font-medium leading-6 text-white">{t(config.dialog.title)}</h2>
        <button
          type="button"
          aria-label={t(config.dialog.closeLabel)}
          className="flex size-5 items-center justify-center text-white transition-colors hover:text-[#D4D4D8]"
          onClick={onClose}
        >
          <X className="size-5" strokeWidth={1.67} />
        </button>
      </header>
      <div className="pb-5">{children}</div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium leading-5 text-white">{label}</span>
      {children}
    </label>
  );
}

const FEEDBACK_TEXT_MAX_LENGTH = 300;

/** 统一的反馈输入框：内置 300 字上限（截断 + maxLength）与字数统计，可选错误提示行 */
function FeedbackTextarea({
  value,
  placeholder,
  onValueChange,
  error,
  maxLength = FEEDBACK_TEXT_MAX_LENGTH,
}: {
  value: string;
  placeholder: string;
  onValueChange: (value: string) => void;
  error?: string;
  maxLength?: number;
}) {
  return (
    <>
      <textarea
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        className="h-30 w-full resize-none rounded-lg border border-[#3F3F47] bg-[#27272A] px-3 py-2 text-sm font-normal leading-5 text-[#D4D4D8] outline-none placeholder:text-[#71717B] focus:border-[#8B3FFC]"
        onChange={(event) => onValueChange(event.target.value.slice(0, maxLength))}
      />
      <div className="mt-1 flex items-center justify-between">
        {error ? <p className="text-xs font-normal leading-4 text-[#FF5C5C]">{error}</p> : <span />}
        <p className="text-xs text-[#71717B]">
          {value.length}/{maxLength}
        </p>
      </div>
    </>
  );
}

function IssueTypeMenu({
  options,
  value,
  onSelect,
}: {
  options: FeedbackOption[];
  value: string;
  onSelect: (value: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="absolute left-0 top-11 z-10 max-h-51.5 w-full overflow-y-auto rounded-lg border border-[#3F3F47] bg-[#1A1A1A] py-1 shadow-2xl">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={cn(
            'flex h-7.5 w-full items-center justify-between px-3 text-left text-sm font-normal leading-5 transition-colors hover:bg-white/5',
            value === option.value ? 'text-[#A772FF]' : 'text-[#D4D4D8]',
          )}
          onClick={() => onSelect(option.value)}
        >
          <span>{t(option.label)}</span>
          {value === option.value && <span className="size-1.5 rounded-full bg-[#A772FF]" />}
        </button>
      ))}
    </div>
  );
}

function ReasonChip({
  reason,
  selected,
  onClick,
}: {
  reason: FeedbackOption;
  selected: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      aria-pressed={selected}
      className={cn(
        'h-8.75 rounded-full border px-3 text-sm font-medium leading-5.25 transition-colors',
        selected
          ? 'border-[#8B3FFC] bg-[#A772FF4D] text-[#A772FF]'
          : 'border-[#3F3F47] bg-[#27272A] text-[#D4D4D8] hover:border-[#8B3FFC]/70',
      )}
      onClick={onClick}
    >
      {t(reason.label)}
    </button>
  );
}

function FeedbackActions({
  submitLabel,
  disabled,
  loading = false,
  onCancel,
  onSubmit,
  className,
}: {
  submitLabel: string;
  disabled?: boolean;
  loading?: boolean;
  onCancel?: () => void;
  onSubmit: () => void;
  className?: string;
}) {
  const { t } = useTranslation();

  return (
    <div className={cn('flex min-h-11 items-start justify-end gap-3 pt-2', className)}>
      <button
        type="button"
        disabled={loading}
        className={cn(
          'flex h-9 items-center justify-center rounded-lg px-4 text-sm font-medium leading-5 text-[#D4D4D8] transition-colors hover:bg-white/5',
          loading && 'cursor-not-allowed opacity-50',
        )}
        onClick={onCancel}
      >
        {t(config.dialog.cancelLabel)}
      </button>
      <button
        type="button"
        disabled={disabled}
        className={cn(
          'flex h-9 items-center justify-center rounded-lg bg-[#2F3137] px-4 text-sm font-medium leading-5 transition-colors',
          disabled
            ? 'cursor-not-allowed text-[#757575] opacity-50'
            : 'text-white hover:bg-[#3A3C43]',
        )}
        onClick={onSubmit}
      >
        {loading && <Loader2 className="mr-2 size-4 animate-spin" strokeWidth={2} />}
        {submitLabel}
      </button>
    </div>
  );
}

function SelectionIcon({ selected }: { selected: boolean }) {
  if (selected) {
    return (
      <span className="flex size-4 shrink-0 items-center justify-center rounded-full bg-white text-black">
        <Check className="size-3" strokeWidth={2.2} aria-hidden="true" />
      </span>
    );
  }

  return <Circle className="size-4 shrink-0 text-[#5A5A5A]" strokeWidth={1.2} aria-hidden="true" />;
}
